import { Controller } from "@hotwired/stimulus"

let nextIndex = Date.now()

export default class extends Controller {
  static targets = ["list", "template", "empty", "count"]
  static values = {
    placeholder: { type: String, default: "NEW_INDEX" },
    tints: { type: Array, default: [] },
  }

  add() {
    this.addRow()
  }

  addRow() {
    const html = this.templateTarget.innerHTML.replaceAll(this.placeholderValue, nextIndex++)
    this.listTarget.insertAdjacentHTML("beforeend", html)
    this.refreshEmpty()
    this.refreshCount()
    this.renumber()
    return this.listTarget.lastElementChild
  }

  duplicate(event) {
    const source = event.target.closest("[data-pending-rows-row]")
    if (!source || !this.rows().includes(source) || !source.dataset.pendingRowsNamePrefix) return

    const index = nextIndex++
    const copy = source.cloneNode(true)
    copy.hidden = false
    // Names move before values: setting a checked radio's name unchecks its group.
    this.rename(copy, source.dataset.pendingRowsNamePrefix, index)
    this.renameDisclosures(copy, source.id, index)
    this.copyValues(source, copy)
    this.dropDestroyedRows(copy)
    this.dropIdentity(copy)
    this.dropTransients(copy)
    this.nameCopy(copy)
    this.unfold(copy)
    this.listTarget.appendChild(copy)
    copy.scrollIntoView({ block: "nearest" })
    this.refreshEmpty()
    this.refreshCount()
    this.renumber()
    return copy
  }

  // After copyValues, which needs both rows the same shape.
  dropDestroyedRows(copy) {
    copy.querySelectorAll("[data-pending-rows-destroy]").forEach((field) => {
      const row = field.closest("[data-pending-rows-row]")
      if (field.value === "1" && row && row !== copy) row.remove()
    })
  }

  dropTransients(copy) {
    copy.querySelectorAll("[data-pending-rows-transient]").forEach((el) => el.remove())
  }

  dropIdentity(copy) {
    this.eachElement(copy, (el) => {
      if (el.dataset.pendingRowsDestroy !== undefined || el.dataset.pendingRowsIdentity !== undefined) {
        el.remove()
      }
    })
  }

  rename(row, prefix, index) {
    if (!prefix) return

    const renamed = prefix.replace(/\[[^\[\]]*\]$/, `[${index}]`)
    this.eachElement(row, (el) => {
      if (el.name?.startsWith(prefix)) el.name = renamed + el.name.slice(prefix.length)
    })
    row.dataset.pendingRowsNamePrefix = renamed
  }

  // Re-keyed on the unique index, not the rank: a saved row's id keys its state.
  renameDisclosures(row, before, index) {
    if (!before) return

    const after = before.replace(/[^-]*$/, index)
    // The root carries a key too, and eachElement walks descendants only.
    this.rekey(row, before, after)
    this.eachElement(row, (el) => this.rekey(el, before, after))
  }

  rekey(el, before, after) {
    ;["id", "aria-controls", "data-disclosure-id-value"].forEach((name) => {
      const value = el.getAttribute?.(name)
      if (value?.includes(before)) el.setAttribute(name, value.replaceAll(before, after))
    })
  }

  copyValues(source, copy) {
    const from = source.querySelectorAll("input, select, textarea")
    const to = copy.querySelectorAll("input, select, textarea")
    from.forEach((control, position) => {
      const twin = to[position]
      if (!twin) return

      if (control.type === "checkbox" || control.type === "radio") twin.checked = control.checked
      else twin.value = control.value
    })
  }

  // The name is trimmed to leave the marker room, never the other way round.
  nameCopy(copy) {
    const field = copy.querySelector("[data-pending-rows-copy-suffix]")
    if (!field) return

    const marker = field.dataset.pendingRowsCopySuffix || ""
    const room = field.maxLength > 0 ? field.maxLength - (marker.length + 1) : Infinity
    const named = field.value.trim() || this.fallbackName(copy)
    const name = (marker && named.endsWith(marker) ? named.slice(0, -marker.length).trim() : named)
      .slice(0, Math.max(room, 0))
    field.value = [name, marker].filter(Boolean).join(" ")
  }

  fallbackName(copy) {
    const picked = copy.querySelector("[data-pending-rows-copy-fallback]:checked")
    return picked?.closest("label")?.textContent.trim() || ""
  }

  // The source row may be folded shut, and a copy that lands at the bottom of a
  // long list behind a chevron is a click that appears to do nothing.
  unfold(copy) {
    const body = copy.querySelector("[data-disclosure-target='body']")
    const header = copy.querySelector("[data-disclosure-target='header']")
    if (body) body.hidden = false
    if (header) header.setAttribute("aria-expanded", "true")
    copy.setAttribute("data-disclosure-state", "open")
  }

  // The server's numbering only returns at the save. A row may show its rank
  // more than once, and its tint is that same rank, so both are rewritten here.
  renumber() {
    this.visibleRows()
      .forEach((row, position) => {
        row.querySelectorAll("[data-pending-rows-ordinal]").forEach((label) => {
          label.textContent = label.dataset.pendingRowsOrdinal.replace("__n__", position + 1)
        })
        this.applyTint(row, position)
      })
  }

  applyTint(row, position) {
    if (this.tintsValue.length === 0) return

    row.classList.remove(...this.tintsValue)
    row.classList.add(this.tintsValue[position % this.tintsValue.length])
  }

  // A <template>'s content is detached, so querySelectorAll walks past it — and
  // the row's own add-buttons clone those templates.
  eachElement(root, visit) {
    Array.from(root.querySelectorAll("*")).forEach((el) => {
      if (el.tagName === "TEMPLATE") this.eachElement(el.content, visit)
      else visit(el)
    })
  }

  remove(event) {
    const row = event.target.closest("[data-pending-rows-row]")
    if (row) this.discard(row)
  }

  discardAll() {
    this.rows().forEach((row) => this.discard(row))
  }

  discard(row) {
    const destroyField = this.destroyFieldOf(row)
    if (destroyField) {
      destroyField.value = "1"
      row.hidden = true
    } else {
      row.remove()
    }
    this.refreshEmpty()
    this.refreshCount()
    this.renumber()
  }

  // Both ways round: removing the last row has to put the empty line back, or
  // the card is left blank under a counter that already reads zero.
  refreshEmpty() {
    const filled = this.visibleRows().length > 0
    if (this.hasEmptyTarget) this.emptyTarget.hidden = filled
    // The `hidden` CLASS, not the attribute: Tailwind's display utilities outrank the preflight [hidden] rule.
    const wrapper = this.listTarget.closest("[data-pending-rows-reveal]")
    if (wrapper) wrapper.classList.toggle("hidden", !filled)
  }

  refreshCount() {
    if (!this.hasCountTarget) return

    this.countTarget.textContent = this.countTarget.textContent.replace(/\d+/, this.visibleRows().length)
  }

  visibleRows() {
    return this.rows().filter((row) => !row.hidden)
  }

  rows() {
    return Array.from(this.listTarget.querySelectorAll("[data-pending-rows-row]")).filter(
      (row) => row.closest("[data-pending-rows-target='list']") === this.listTarget,
    )
  }

  destroyFieldOf(row) {
    return Array.from(row.querySelectorAll("[data-pending-rows-destroy]")).find(
      (el) => el.closest("[data-pending-rows-row]") === row,
    )
  }
}
